import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Spinner from '../components/Spinner';
import { parseBibtexString, displayPaper } from '../utils/bibtexParser';
import PublicationsModal from '../components/PublicationsModal';
import '../styles/MemberPage.css';
import '../styles/PublicationsPage.css';

const MemberPage = () => {
  const { id } = useParams(); // Obtener ID del miembro desde la URL
  const [member, setMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about'); // Estado para gestionar la pestaña activa
  const [publications, setPublications] = useState([]); // Inicializar como array vacío
  const [pubLoading, setPubLoading] = useState(false); // Estado de carga para publicaciones
  const [selectedBibtex, setSelectedBibtex] = useState('');
  const [isModalOpen, setModalOpen] = useState(false);


  useEffect(() => {
    // Cargar datos desde members.json
    fetch('/data/members.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch members.json');
        }
        return response.json();
      })
      .then((data) => {
        // Buscar miembro por ID
        const selectedMember = data.find((m) => m.id === parseInt(id, 10));
        setMember(selectedMember);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching members.json:', error);
        setIsLoading(false);
      });
  }, [id]);

  const loadPublications = () => {
    if (!member || !member.personal_folder) return;
    setPubLoading(true);
    fetch(`/data/${member.personal_folder}/papers.bib`)
      .then((response) => {
        if (!response.ok) {
          console.warn('No papers.bib found for this member');
          setPublications([]);
          setPubLoading(false);
          return null;
        }
        return response.text();
      })
      .then((bibtexData) => {
        if (bibtexData) {
          const parsedPublications = parseBibtexString(bibtexData);
          if (parsedPublications) {
            // Agregar el campo `bibtex` directamente
            const enrichedPublications = parsedPublications.map((pub) => ({
              ...pub,
              bibtex: pub.raw || '', // Usa el campo `raw` como `bibtex`
            }));
            setPublications(enrichedPublications);
          } else {
            setPublications([]);
          }
        }
        setPubLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching papers.bib:', error);
        setPublications([]);
        setPubLoading(false);
      });
  };


  if (isLoading) {
    return <Spinner />;
  }

  if (!member) {
    return <p>Member not found</p>;
  }

  const handleError = (event) => {
    event.target.src = '/default_pfp.png';
  };

  const handleShowBibtex = (bibtex) => {
    // console.log('Selected BibTeX:', bibtex);
    setSelectedBibtex(bibtex);
    setModalOpen(true);
  };


  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'publications') {
      loadPublications();
    }
  };

  return (
    <div className="member-page">
      <div className="member-header">
        <img
          src={`/data/${member.personal_folder}/pfp.jpg`}
          alt={member.name}
          className="member-image"
          onError={handleError}
        />
        <h2>{member.name}</h2>
        <p>{member.position}</p>
        <ul className="research-keywords">
          {member.specializations.map((spec, index) => (
            <li key={index}>{spec}</li>
          ))}
        </ul>
        {member.social_links&& <div className="social-links">
          {member.social_links.scholar && <a
            href={member.social_links.scholar}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
          >
            <img src="https://img.icons8.com/?size=100&id=drPiDBy9kkJ3&format=png&color=000000" alt="Google Scholar" width={35} />
          </a>}
          {member.social_links.orcid &&<a
            href={member.social_links.orcid}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
          >
            <img src="https://www.svgrepo.com/show/349469/orcid.svg" alt="ORCID" width={32} />
          </a>}
          {member.social_links.github &&<a
            href={member.social_links.github}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
          >
            <img src="https://www.svgrepo.com/show/448225/github.svg" alt="GitHub" width={35} />
          </a>}
          {member.social_links.linkedin &&<a
            href={member.social_links.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
          >
            <img src="https://www.svgrepo.com/show/452051/linkedin.svg" alt="LinkedIn" width={35} />
          </a>}
        </div>}
      </div>

      {/* Dual selector */}
      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => handleTabChange('about')}
        >
          About
        </button>
        <button
          className={`tab-button ${activeTab === 'publications' ? 'active' : ''}`}
          onClick={() => handleTabChange('publications')}
        >
          Publications
        </button>
      </div>

      {/* Tab content */}
      <div className="tab-content">
        {activeTab === 'about' && (
          <div className="about-content">
            <p>{member.about || 'No information available.'}</p>
          </div>
        )}
        {activeTab === 'publications' && (
          <div className="publications-content">
            {pubLoading ? (
              <Spinner />
            ) : publications.length > 0 ? (
              publications.map((pub, index) => {
                // console.log('Publication:', pub);
                return (
                  <div key={index} className="publication-item">
                    {displayPaper(pub, handleShowBibtex)}
                  </div>
                );
              })
            ) : (
              <p>No publications found for this member.</p>
            )}
            <PublicationsModal isOpen={isModalOpen} onClose={handleCloseModal} content={selectedBibtex} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberPage;
